import React, { useState } from 'react'
import { Typography, Stack } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { TextField } from '@e3dc-react/shell/Elements/InputFields'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { AccessRight, AccessRightOptions } from 'src/Routes/Installation/InstallationModel'
import InstallationAccessRequestDialogTable from './InstallationAccessRequestDialogTable'
import { checkIsDateLessThanTwoWeeks, getInstallationAccessRequestTextDescription } from '../../utils'
import { sendAccessRequest } from '../../api'
import { useDispatch } from 'react-redux'

export interface InstallationAccessRequestDialogProps {
  onClose?: () => void
  place?: number
  installationId: number
  accountLinkId: number | undefined
  storageId: string | undefined
  owner: string
  permissionType: AccessRight
  accessRequestAt?: string
}

/**
 * Dialog component to send access request to owner.
 */
const InstallationAccessRequestDialog: React.FC<InstallationAccessRequestDialogProps> = ({
  onClose,
  installationId,
  storageId,
  owner,
  permissionType,
  accountLinkId,
  accessRequestAt,
  place,
}) => {
  const { t } = useCustomTranslation()
  const [reason, setReason] = useState('')
  const authFetch = useAuthFetchWithResponse()
  const dispatch = useDispatch()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()

  const hasExpiredPendingRequest = permissionType === AccessRightOptions.pending && accessRequestAt && !checkIsDateLessThanTwoWeeks(accessRequestAt)
  const canSendRequest = permissionType === AccessRightOptions.noAccess || hasExpiredPendingRequest
  const isCharactersConditionFullfilled = reason.length > 0 && reason.length <= 255

  const textDescription = getInstallationAccessRequestTextDescription(permissionType, t)

  const handleOnSave = async (): Promise<void> => {
    if (accountLinkId === undefined || storageId === undefined) return
    const { hasErrors } = await sendAccessRequest(installationId, storageId, accountLinkId, reason, authFetch)

    if (hasErrors) {
      enqueueErrorStack(t('installation.access-request-dialog.send-request-failure-message'))
      return
    }
    enqueueSuccessStack(t('installation.access-request-dialog.send-request-success-message'))
    dispatch(refreshApiView())
    onClose?.()
  }

  const modalActions = {
    primary: {
      name: t('installation.access-request-dialog.send-request'),
      onClick: handleOnSave,
      disabled: !canSendRequest || !isCharactersConditionFullfilled,
    },
    secondary: { name: t('common.cancel') },
  }

  const handleTextChange = (value: string, name: string): void => {
    setReason(value)
  }

  return (
    <Dialog maxWidth="sm" title={t('installation.access-request-dialog.title')} actions={modalActions} onClose={onClose}>
      <Stack mt={4} gap={4}>
        {canSendRequest ? (
          <>
            <InstallationAccessRequestDialogTable installationId={installationId} storageId={storageId} owner={owner} />
            <Stack>
              <TextField onChange={handleTextChange} value={reason} label={t('installation.access-request-dialog.input-label')} multiline fullWidth />
              <Stack direction="row" justifyContent="flex-end">
                <Typography padding={1} variant="caption">
                  {reason.length} / 255
                </Typography>
              </Stack>
            </Stack>
          </>
        ) : (
          <Typography variant="body2">{textDescription}</Typography>
        )}
      </Stack>
    </Dialog>
  )
}

export default InstallationAccessRequestDialog
